import { useRef, useEffect, useCallback, MutableRefObject } from 'react';
import { BrightcovePlayerRef } from '../AVideo.types';

export interface UseVideoPlayStateOptions {
  isPlaying?: boolean;
  onAutoplayBlocked?: () => void;
}

export interface UseVideoPlayStateResult {
  playerRef: MutableRefObject<BrightcovePlayerRef | null>;
  pendingIsPlayingRef: MutableRefObject<boolean | undefined>;
  /**
   * Call inside your player's onSuccess/onReady handler.
   * Sets playerRef.current and applies any isPlaying intent that arrived before the player was ready.
   */
  applyPendingPlayState: (player: BrightcovePlayerRef) => void;
}

/**
 * Shared imperative play/pause hook for managed video players (Brightcove and future players).
 * Holds the pending-play ref pattern so player sub-components do not re-implement it.
 *
 * Uses a stable ref for onAutoplayBlocked to avoid stale closures in effects.
 *
 * Race-condition safety: applyPendingPlayState sets playerRef.current before
 * reading pendingIsPlayingRef, so the isPlaying effect and this callback never
 * disagree about whether the player is ready. Under React 18 concurrent mode
 * the effect may re-run, but because both paths converge on playerRef.current
 * the worst case is a redundant play()/pause() call, never a missed one.
 */
export function useVideoPlayState({ isPlaying, onAutoplayBlocked }: UseVideoPlayStateOptions): UseVideoPlayStateResult {
  const playerRef = useRef<BrightcovePlayerRef | null>(null);
  const pendingIsPlayingRef = useRef<boolean | undefined>(undefined);
  const onAutoplayBlockedRef = useRef(onAutoplayBlocked);

  useEffect(() => {
    onAutoplayBlockedRef.current = onAutoplayBlocked;
  }, [onAutoplayBlocked]);

  useEffect(() => {
    if (isPlaying === undefined) return;

    if (playerRef.current) {
      if (isPlaying) {
        playerRef.current.play?.().catch(() => {
          onAutoplayBlockedRef.current?.();
        });
      } else {
        playerRef.current.pause?.();
      }
    } else {
      pendingIsPlayingRef.current = isPlaying;
    }
  }, [isPlaying]);

  const applyPendingPlayState = useCallback((player: BrightcovePlayerRef) => {
    // Set playerRef before reading pending state so the isPlaying effect
    // and this callback never disagree about whether the player is ready.
    playerRef.current = player;
    const pending = pendingIsPlayingRef.current;
    pendingIsPlayingRef.current = undefined;

    if (pending === true) {
      player.play?.().catch(() => {
        onAutoplayBlockedRef.current?.();
      });
    } else if (pending === false) {
      player.pause?.();
    }
  }, []);

  return { playerRef, pendingIsPlayingRef, applyPendingPlayState };
}
